import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import "./MapComponent.css";
import { Link, useNavigate } from "react-router-dom";
import { getCsrfToken } from "../../services/csrfTokenManager";
import * as Toggle from "@radix-ui/react-toggle";

const API_URL = process.env.REACT_APP_API_URL;

const getGoogleMapsUrl = (lat, lng, origin = null) => {
  let url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  if (origin) {
    url += `&origin=${origin.lat},${origin.lng}`;
  }
  return url;
};

const renderContactInfo = (name, phone, email) => {
  // If the name is missing, return an empty string (i.e., don't display anything for this contact)
  if (!name) return "";

  // If the name exists, display it and any other available info (phone, email)
  return `
    <p>
      <strong>${name}</strong><br />
      ${phone ? `Phone: <a href="tel:${phone}">${phone}</a><br />` : ""}
      ${email ? `Email: <a href="mailto:${email}">${email}</a>` : ""}
    </p>
  `;
};

const ensureHttp = (url) => {
  if (!url) return null; // Return null if the URL is empty or null
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    return `http://${url}`;
  }
  return url;
};

const createInfoWindowContent = (church, userLocation) => {
  const websiteUrl = ensureHttp(church["Website"]);
  const facebookUrl = ensureHttp(church["Facebook Link"]);

  return `
    <div style="font-family: Arial, sans-serif; font-size: 14px; width: 300px; max-height: 400px; overflow-y: auto;">
      <h3 style="margin: 0 0 10px; font-size: 18px;">${church.name}</h3>
      <p>
        <strong>Address: </strong>
        <a href="${getGoogleMapsUrl(
          church.lat,
          church.lon,
          userLocation
        )}" target="_blank" rel="noopener noreferrer" style="color: #0066cc; text-decoration: none;">
          ${church.address}
        </a>
      </p>
      <p><strong>Sunday AM:</strong> ${church["Sunday AM"]}</p>
      ${
        church["Sunday PM"]
          ? `<p><strong>Sunday PM:</strong> ${church["Sunday PM"]}</p>`
          : ""
      }
      ${
        church["Midweek"]
          ? `<p><strong>Midweek:</strong> ${church["Midweek"]}</p>`
          : ""
      }
      
      <h4 style="margin: 10px 0 5px;">Contacts:</h4>
      ${renderContactInfo(
        church["Contact 1 Name"],
        church["Contact 1 Phone number"],
        church["Contact 1 email"]
      )}
      ${renderContactInfo(
        church["Contact 2 Name"],
        church["Contact 2 Phone number"],
        church["Contact 2 email"]
      )}
      ${renderContactInfo(
        church["Contact 3 Name"],
        church["Contact 3 Phone number"],
        church["Contact 3 email"]
      )}

      ${
        websiteUrl
          ? `<p><strong>Website:</strong> <a href="${websiteUrl}" target="_blank" rel="noopener noreferrer" style="color: #0066cc; text-decoration: none;">Visit Website</a></p>`
          : ""
      }
      ${
        facebookUrl
          ? `<p><strong>Facebook:</strong> <a href="${facebookUrl}" target="_blank" rel="noopener noreferrer" style="color: #0066cc; text-decoration: none;">Visit Facebook</a></p>`
          : ""
      }
    </div>
  `;
};

const GoogleMapComponent = () => {
  const [churches, setChurches] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [map, setMap] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const mapRef = useRef(null);
  const infoWindowRef = useRef(null);
  const markersRef = useRef([]);
  const navigate = useNavigate();
  const [language, setLanguage] = useState("english");

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];
  }, []);

  useEffect(() => {
    if (
      !isLoading &&
      churches.length > 0 &&
      window.google &&
      window.google.maps
    ) {
      clearMarkers();

      churches.forEach((church) => {
        const churchNameLower = church.name.toLowerCase();
        const isSpanish = churchNameLower.includes('spanish');
        const isBilingual = churchNameLower.includes('bilingual');
        
        if (
          (language === 'spanish' && (isSpanish || isBilingual)) ||
          (language === 'english' && (!isSpanish || isBilingual))
        ) {
          // Create and add marker for this church
          if (typeof church.lat === 'number' && typeof church.lon === 'number' && !isNaN(church.lat) && !isNaN(church.lon)) {
            const marker = new window.google.maps.Marker({
              map: map,
              position: { lat: church.lat, lng: church.lon },
              title: church.name
            });
            marker.addListener('click', () => {
              infoWindowRef.current.setContent(createInfoWindowContent(church));
              infoWindowRef.current.open(map, marker);
            });
            markersRef.current.push(marker);
          }
        }
      });
      
    }
  }, [isLoading, churches, language, map, clearMarkers]);

  const axiosInstance = useRef(
    axios.create({
      baseURL: API_URL,
      withCredentials: true,
    })
  );

  useEffect(() => {
    const instance = axiosInstance.current;
    const interceptor = instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          navigate("/login", { state: { from: window.location.pathname } });
        }

        return Promise.reject(error);
      }
    );

    return () => {
      instance.interceptors.response.eject(interceptor);
    };
  }, [navigate, axiosInstance]);

  const initMap = useCallback(() => {
    if (
      !mapRef.current ||
      !window.google ||
      !window.google.maps ||
      churches.length === 0
    )
      return;

    const googleMaps = window.google.maps;
    const newMap = new googleMaps.Map(mapRef.current, {
      center: { lat: 39.8283, lng: -98.5795 },
      zoom: 5,
      mapTypeControl: false,
      streetViewControl: false,
      mapTypeId: "roadmap",
      scrollwheel: true,
      gestureHandling: "greedy",
    });
    setMap(newMap);
    infoWindowRef.current = new googleMaps.InfoWindow();

    clearMarkers();

    churches.forEach((church) => {
      if (
        typeof church.lat === "number" &&
        typeof church.lon === "number" &&
        !isNaN(church.lat) &&
        !isNaN(church.lon)
      ) {
        const marker = new googleMaps.Marker({
          map: newMap,
          position: { lat: church.lat, lng: church.lon },
          title: church.name,
        });
        marker.addListener("click", () => {
          infoWindowRef.current.setContent(createInfoWindowContent(church));
          infoWindowRef.current.open(newMap, marker);
        });
        markersRef.current.push(marker);
      }
    });
  }, [churches, clearMarkers]);

  const loadGoogleMapsApi = useCallback(async () => {
    if (window.google && window.google.maps) {
      return window.google.maps;
    }

    const csrfToken = await getCsrfToken();
    try {
      const response = await axiosInstance.current.get("/maps/js", {
        headers: { "X-CSRF-Token": csrfToken },
      });

      return new Promise((resolve, reject) => {
        window.initMap = () => {
          if (window.google && window.google.maps) {
            resolve(window.google.maps);
          } else {
            reject(new Error("Google Maps failed to load"));
          }
        };

        const script = document.createElement("script");
        script.innerHTML = response.data;
        script.async = true;
        script.defer = true;
        script.onerror = () =>
          reject(new Error("Failed to load Google Maps script"));
        document.head.appendChild(script);
      });
    } catch (error) {
      console.error("Error fetching Google Maps script:", error);
      throw error;
    }
  }, []);
  const fetchChurches = useCallback(async () => {
    try {
      const csrfToken = await getCsrfToken();
      const response = await axiosInstance.current.get("/churches", {
        headers: { "X-CSRF-Token": csrfToken },
      });
      const validChurches = response.data.filter(
        (church) =>
          typeof church.lat === "number" &&
          typeof church.lon === "number" &&
          !isNaN(church.lat) &&
          !isNaN(church.lon)
      );
      setChurches(validChurches);
    } catch (error) {
      console.error("Error fetching churches:", error);
      setError("Failed to load church data. Please try again later.");
    }
  }, []);

  useEffect(() => {
    const initializeMap = async () => {
      setIsLoading(true);
      try {
        await loadGoogleMapsApi();
        await fetchChurches();
        setIsLoading(false);
      } catch (error) {
        console.error("Error initializing map:", error);
        setError("Failed to load the map. Please try again later.");
        setIsLoading(false);
      }
    };

    initializeMap();
  }, [loadGoogleMapsApi, fetchChurches]);

  useEffect(() => {
    if (
      !isLoading &&
      churches.length > 0 &&
      window.google &&
      window.google.maps
    ) {
      initMap();
    }
  }, [isLoading, churches, initMap]);

  const handleSearchInputChange = useCallback(async (e) => {
    const input = e.target.value;
    setSearchInput(input);

    if (input.length > 2) {
      try {
        const csrfToken = await getCsrfToken();
        const response = await axiosInstance.current.get(
          "/places/autocomplete",
          {
            params: { input },
            headers: { "X-CSRF-Token": csrfToken },
          }
        );
        setSearchResults(response.data);
      } catch (error) {
        console.error("Error fetching place predictions:", error);
      }
    } else {
      setSearchResults([]);
    }
  }, []);

  const handlePlaceSelect = useCallback(
    async (placeId) => {
      try {
        const csrfToken = await getCsrfToken();
        const response = await axiosInstance.current.get("/places/details", {
          params: { placeId },
          headers: { "X-CSRF-Token": csrfToken },
        });
        const location = response.data.geometry.location;

        if (
          map &&
          location &&
          typeof location.lat === "number" &&
          typeof location.lng === "number"
        ) {
          map.panTo(location);
          map.setZoom(10);

          new window.google.maps.Marker({
            position: location,
            map: map,
            icon: {
              url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
              scaledSize: new window.google.maps.Size(50, 50),
            },
          });
        }

        setSearchInput("");
        setSearchResults([]);
      } catch (error) {
        console.error("Error fetching place details:", error);
      }
    },
    [map]
  );

  if (isLoading) {
    return <div>Loading map...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div className="google-map-section">
      <div className="map-header space-y-4">
        <h2 className="text-2xl font-bold">Find a Church Near You</h2>
        <div className="flex flex-col items-center">
          <p className="text-base font-semibold mb-2">
            Select Service Language:
          </p>
          <div className="flex items-center bg-gray-200 rounded-full p-1 w-64 shadow-md">
            <Toggle.Root
              className={`flex-1 py-3 text-center rounded-full transition-all font-medium ${
                language === "english"
                  ? "bg-blue-500 text-white"
                  : "bg-transparent text-gray-700"
              }`}
              pressed={language === "english"}
              onPressedChange={() => setLanguage("english")}
            >
              English
            </Toggle.Root>
            <Toggle.Root
              className={`flex-1 py-3 text-center rounded-full transition-all font-medium ${
                language === "spanish"
                  ? "bg-blue-500 text-white"
                  : "bg-transparent text-gray-700"
              }`}
              pressed={language === "spanish"}
              onPressedChange={() => setLanguage("spanish")}
            >
              Spanish
            </Toggle.Root>
          </div>
        </div>
      </div>
      <div className="map-wrapper">
        <div className="map-container">
          <div className="search-box">
            <input
              className="search-input"
              type="text"
              placeholder="Search for a location"
              value={searchInput}
              onChange={handleSearchInputChange}
            />
            {searchResults.length > 0 && (
              <ul className="search-results">
                {searchResults.map((result) => (
                  <li
                    key={result.place_id}
                    onClick={() => handlePlaceSelect(result.place_id)}
                  >
                    {result.description}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div ref={mapRef} style={{ height: "100%", width: "100%" }} />
        </div>
      </div>

      <div className="map-footer">
        <p>If you notice any errors on the map, please let us know.</p>
        <div className="button-container">
          <Link to="/contact" className="contact-button">
            Contact Us
          </Link>
          <a
            className="dbox-donation-page-button"
            href="https://donorbox.org/my-church-of-christ?default_interval=o"
            style={{
              background: "rgb(18, 138, 237)",
              color: "white",
              textDecoration: "none",
              fontFamily: "Verdana, sans-serif",
              display: "inline-flex",
              alignItems: "center",
              fontSize: "16px",
              padding: "8px 24px",
              borderRadius: "5px",
              gap: "8px",
              lineHeight: "24px",
            }}
          >
            <img
              src="https://donorbox.org/images/white_logo.svg"
              alt="Donorbox Logo"
              style={{ marginRight: "8px" }}
            />
            Donate
          </a>
        </div>
      </div>
    </div>
  );
};

export default GoogleMapComponent;
